import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RotateCw } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { useCreditNotes, CREDIT_NOTE_PAGE_SIZE } from '../hooks/useCreditNotes';
import { RuleTiles } from '../components/credit-notes/RuleTiles';
import { CreditNoteFiltersBar } from '../components/credit-notes/CreditNoteFilters';
import { CreditNoteTable, type SortKey } from '../components/credit-notes/CreditNoteTable';
import { CreditNoteDrawer } from '../components/credit-notes/CreditNoteDrawer';
import { RecourseTab } from '../components/credit-notes/RecourseTab';
import { TrendsTab } from '../components/credit-notes/TrendsTab';
import { creditNotesApi } from '../services/creditNotesApi';
import { formatAmount, formatCount, formatCredited, freshnessTone } from '../utils/creditNoteFormat';
import { formatDateTimeCH } from '../utils/format';
import { creditNoteKey, parseCreditNoteKey, type CreditNoteDocument } from '../types/creditNotes';

/**
 * Gutschriften-Kontrolle — every customer credit note, why it was issued, whether it was booked
 * correctly, and what is still to be claimed back from somebody else.
 *
 * P4 MADE IT WRITE. Three tracks: the app's own reason (one group vocabulary, prefilled from the
 * ERP where `GU_Category_BIG` carries a code and not overwritable there), the booking-check
 * status, and the recourse case — the two status tracks independent of each other, because a
 * credit note can be booked perfectly correctly and still be open against a supplier.
 *
 * THE PAGE OWNS NO COUNTS. Every number on it — the tiles, the KPIs, the exclusions — comes out
 * of `summary`, computed server-side over the same evaluation the `?rule=` filter uses. The one
 * discipline that matters here is not adding a figure the page derives itself, because a tile
 * whose click cannot reproduce its own number is the failure mode this whole design avoids.
 *
 * THE URL IS THE DEEP LINK. `?rule=`, `?company=` and `?doc=dbi-519054` survive a reload and a
 * paste into a mail, which is what makes a finding shareable: Stefan can send Roman the exact
 * document rather than a description of how to find it.
 */
export function CreditNotesPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/credit-notes');
    const { toast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const urlRule = searchParams.get('rule') ?? '';
    const urlCompany = searchParams.get('company') ?? '';
    const urlDoc = searchParams.get('doc') ?? '';

    /**
     * A `?doc=` deep link is resolved ONCE, into the search box and the company filter.
     *
     * Without this the link only works when the document happens to be on the first page: the
     * drawer resolves against the rows actually loaded, and 501 documents over 12 months are six
     * pages. Worse than not opening, the "not in the current period or filter" banner would then
     * be actively misleading — the document IS in the period, it is on page three. Seeding the
     * search puts the row on screen AND shows in the filter bar why the list is narrowed, which
     * is the same resolution the customer-duplicates page uses for its `?customer=` links.
     */
    const deepLinkSeed = useMemo(() => {
        const parsed = urlDoc ? parseCreditNoteKey(urlDoc) : null;
        if (!parsed) return { rule: urlRule, companyCode: urlCompany };
        return { rule: urlRule, companyCode: parsed.companyCode, search: parsed.documentNo };
    }, [urlDoc, urlRule, urlCompany]);

    const {
        documents, summary, freshness, availableCompanies, reasonCatalog, vocabularies, total,
        loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages, reload,
    } = useCreditNotes(canView, deepLinkSeed);

    // The URL is the source of truth for the two filters that are worth sharing; everything
    // else (search text, period) is session-local and not worth a history entry.
    const putUrl = useCallback((next: Record<string, string>) => {
        setSearchParams(prev => {
            const sp = new URLSearchParams(prev);
            for (const [k, v] of Object.entries(next)) {
                if (v) sp.set(k, v); else sp.delete(k);
            }
            return sp;
        }, { replace: true });
    }, [setSearchParams]);

    const selectRule = useCallback((rule: string) => {
        setFilters({ rule });
        putUrl({ rule });
    }, [setFilters, putUrl]);

    const changeFilters = useCallback((next: Parameters<typeof setFilters>[0]) => {
        setFilters(next);
        if (next.companyCode !== undefined) putUrl({ company: next.companyCode });
    }, [setFilters, putUrl]);

    const handleClear = useCallback(() => {
        clearFilters();
        putUrl({ rule: '', company: '' });
    }, [clearFilters, putUrl]);

    /**
     * Three tabs. The canvas draws four; "Rules" is the threshold screen and is reached from the
     * tiles rather than from here, and a tab that opens onto nothing is a promise the page cannot
     * keep. `?tab=trends` and `?tab=recourse` survive a reload and a paste into a mail, like every
     * other piece of this page's state.
     */
    const tabParam = searchParams.get('tab');
    const activeTab: 'credit-notes' | 'trends' | 'recourse' =
        tabParam === 'recourse' ? 'recourse' : tabParam === 'trends' ? 'trends' : 'credit-notes';

    /**
     * Bumped after every write. The list carries the reason, the booking status and the recourse
     * status in its own columns and counts them in its KPIs; the recourse queue reads the same
     * rows from the other side. Without this, the row a reviewer just changed would keep showing
     * what the database no longer holds — the screen wrong while the database is right, which is
     * the harder half of that bug to notice.
     */
    const [writeToken, setWriteToken] = useState(0);
    const handleStateChanged = useCallback(() => {
        setWriteToken(t => t + 1);
        void reload();
    }, [reload]);

    const [selectedKey, setSelectedKey] = useState<string | null>(urlDoc || null);

    const selectedDoc: CreditNoteDocument | null = useMemo(
        () => documents.find(d => creditNoteKey(d.companyCode, d.documentNo) === selectedKey) ?? null,
        [documents, selectedKey],
    );

    // A `?doc=` deep link that is not on the loaded page is not silently ignored — the reader
    // is told, because a link that opens nothing looks like a broken document rather than a
    // document outside the current filters.
    const deepLinkMissing = selectedKey !== null && selectedDoc === null && !loading && documents.length > 0;

    const openDoc = useCallback((doc: CreditNoteDocument) => {
        const key = creditNoteKey(doc.companyCode, doc.documentNo);
        setSelectedKey(key);
        putUrl({ doc: key });
    }, [putUrl]);

    const closeDoc = useCallback(() => {
        setSelectedKey(null);
        putUrl({ doc: '' });
    }, [putUrl]);

    /**
     * A click in the recourse queue opens that credit note.
     *
     * It goes through the SAME resolution a `?doc=` deep link uses — seed the search box and the
     * company filter, then open the drawer — rather than a second mechanism. The drawer resolves
     * against the rows actually loaded, and a document sitting on page four of six would
     * otherwise open nothing. If the credit note is older than the current period the existing
     * banner says so, which is the truth: the queue is not scoped by the period and the list is.
     */
    const openFromRecourse = useCallback((companyCode: string, documentNo: string) => {
        const key = creditNoteKey(companyCode, documentNo);
        setFilters({
            companyCode, search: documentNo,
            rule: '', appReasonCode: '', reviewStatus: '', recourseStatus: '',
        });
        setSelectedKey(key);
        putUrl({ tab: '', doc: key, company: companyCode, rule: '' });
    }, [setFilters, putUrl]);

    const handleCopy = useCallback((value: string) => {
        void navigator.clipboard.writeText(value);
        toast(`Copied ${value}`, 'success');
    }, [toast]);

    useEffect(() => { if (!urlDoc) setSelectedKey(null); }, [urlDoc]);

    const creditedTotal = useMemo(
        () => documents.reduce((sum, d) => sum + Math.abs(d.amountEur ?? 0), 0),
        [documents],
    );

    const notAssessable = useMemo(
        () => documents.filter(d => d.rules.fired.length === 0 && d.rules.notEvaluated.length >= 5).length,
        [documents],
    );

    if (permLoading) {
        return <div className="p-6 text-sm text-gray-500">Loading…</div>;
    }

    if (!canView) {
        return (
            <div className="p-6">
                <h1 className="text-xl font-semibold text-gray-900">Credit Note Control</h1>
                <p className="mt-2 text-sm text-gray-600">You do not have access to this page.</p>
            </div>
        );
    }

    const tone = freshnessTone(freshness.ageInDays);

    return (
        <div className="space-y-4 p-6">
            <div className="flex items-baseline justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Credit Note Control</h1>
                <p className="text-xs italic text-gray-500">
                    Every customer credit note, why it was issued and whether it was booked correctly.
                </p>
            </div>

            {/*
                Three tabs. "Rules" from the canvas is the threshold screen, reached from a tile —
                a tab that opens onto nothing is a promise the page cannot keep, the same reason
                P3 left the recourse column undrawn.
            */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex">
                    <TabButton
                        active={activeTab === 'credit-notes'}
                        onClick={() => putUrl({ tab: '' })}
                        label="Credit notes"
                    />
                    <TabButton
                        active={activeTab === 'trends'}
                        onClick={() => putUrl({ tab: 'trends' })}
                        label="Reasons & trends"
                    />
                    <TabButton
                        active={activeTab === 'recourse'}
                        onClick={() => putUrl({ tab: 'recourse' })}
                        label="Recourse"
                        badge={summary.recourse.openCases > 0 ? formatCount(summary.recourse.openCases) : undefined}
                        badgeTone={summary.recourse.overdueCases > 0 ? 'alert' : 'normal'}
                    />
                </nav>
            </div>

            {activeTab === 'recourse' ? (
                <RecourseTab
                    canView={canView}
                    refreshToken={writeToken}
                    onOpenDocument={openFromRecourse}
                />
            ) : activeTab === 'trends' ? (
                <TrendsTab
                    summary={summary}
                    catalog={reasonCatalog}
                    loading={loading}
                    /* A bar's click IS the reason filter — the same parameter, the same server-side
                       count. The reader lands on the credit notes the bar was drawn from. */
                    onDrillToReason={code => {
                        setFilters({ appReasonCode: code });
                        putUrl({ tab: '' });
                    }}
                    onExport={() => creditNotesApi.export(filters)}
                />
            ) : (
            <>
            {/* KPIs. Every figure comes out of `summary`, computed server-side over the same
                evaluation the filters use — the page derives none of them itself. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <Kpi
                    label={`Credit notes · ${summary.window.dateFrom.slice(0, 7)} to ${summary.window.dateTo.slice(0, 7)}`}
                    value={formatCount(summary.documentsInWindow)}
                    hint={`${Object.keys(summary.byCompany).length} companies · document level`}
                />
                <Kpi
                    label="Credited value on this page"
                    value={`${formatCredited(creditedTotal)}`}
                    hint={`EUR · ${formatCount(documents.length)} of ${formatCount(total)} shown`}
                />
                <Kpi
                    label="Without a reason in the ERP"
                    value={formatCount(summary.documentsInWindow - summary.documentsWithErpReasonCode)}
                    hint="dbi and bin code it; the others do not"
                    tone="warn"
                />
                <Kpi
                    label="Not assessable on this page"
                    value={formatCount(notAssessable)}
                    hint="named result, not a gap — see the drawer"
                />
                {/*
                    THE CANVAS TILE P3 REFUSED TO DRAW, now that it has rows behind it.
                    It is GROUP-WIDE and NOT narrowed by the period above, and the hint says so:
                    a claim does not stop being open because a date filter moved, and an open
                    claim on a fourteen-month-old credit note is the one nobody should lose sight
                    of. Clicking it opens the Recourse tab, which counts the same set through the
                    same server-side function — so the tile and its own click cannot disagree.
                */}
                <button
                    onClick={() => putUrl({ tab: 'recourse' })}
                    className="rounded-lg border border-gray-200 bg-white px-3.5 py-3 text-left hover:bg-gray-50"
                >
                    <div className="text-xs text-gray-500">Recourse open · all periods</div>
                    <div className="mt-0.5 flex items-baseline gap-2">
                        <span className="text-[22px] font-semibold leading-7 text-gray-900">
                            {formatCount(summary.recourse.openCases)}
                        </span>
                        <span className="text-[13px] text-gray-500">
                            {Object.entries(summary.recourse.expectedByCurrency).length === 0
                                ? 'nothing quantified'
                                : Object.entries(summary.recourse.expectedByCurrency)
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([code, value]) => `${formatAmount(value)} ${code}`)
                                    .join(' · ')}
                        </span>
                    </div>
                    <div className={`text-[11px] leading-4 ${
                        summary.recourse.overdueCases > 0 ? 'text-red-700' : 'text-gray-400'
                    }`}>
                        {summary.recourse.overdueCases > 0
                            ? `${formatCount(summary.recourse.overdueCases)} overdue for follow-up`
                            : 'nothing overdue · not narrowed by the period'}
                    </div>
                </button>
            </div>

            <RuleTiles
                tiles={summary.ruleTiles}
                exclusions={summary.exclusions}
                documentsInWindow={summary.documentsInWindow}
                activeRule={filters.rule}
                onSelect={selectRule}
            />

            <CreditNoteFiltersBar
                filters={filters}
                summary={summary}
                availableCompanies={availableCompanies}
                reasonCatalog={reasonCatalog}
                vocabularies={vocabularies}
                activeFilterCount={activeFilterCount}
                onChange={changeFilters}
                onClear={handleClear}
            />

            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <button
                    onClick={() => void reload()}
                    disabled={loading}
                    className="inline-flex h-8 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                    <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Reload table
                </button>
                <span>{formatCount(total)} credit note{total === 1 ? '' : 's'}</span>
                <span className="text-gray-300">·</span>
                {/* The load runs Sunday to Thursday night — Friday and Saturday have no run at
                    all. So "3 days old" on a Sunday is the normal state of this data, and
                    painting it as a fault would teach the reader to ignore the indicator. */}
                <span className={tone === 'stale' ? 'text-amber-700' : undefined}>
                    ERP data as of {freshness.lastEtlRunUtc ? formatDateTimeCH(freshness.lastEtlRunUtc) : 'unknown'}
                    {freshness.ageInDays !== null && ` · ${freshness.ageInDays} d`}
                </span>
                <span className="text-[11px] text-gray-400">{freshness.etlSchedule}</span>
            </div>

            {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            {summary.unmappedErpReasons.documents > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {formatCount(summary.unmappedErpReasons.documents)} credit note
                    {summary.unmappedErpReasons.documents === 1 ? '' : 's'} carry an ERP category that is
                    not one of the ten codes in the group catalogue —{' '}
                    <span className="font-mono">{summary.unmappedErpReasons.values.join(', ')}</span>.
                    An eleventh value has appeared in Business Central. The documents are in the list and
                    show the raw value; the code has to be added to CreditNoteReasonCatalog before it can
                    be filtered or compared.
                </div>
            )}

            {deepLinkMissing && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    The linked document <span className="font-mono">{selectedKey}</span> is not in this period.
                    Widen the period to open it — the link carries no date of its own.
                </div>
            )}

            <CreditNoteTable
                documents={documents}
                skipReasonLabels={summary.skipReasonLabels}
                reasonCatalog={reasonCatalog}
                vocabularies={vocabularies}
                loading={loading}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(key: SortKey) => setSort(key)}
                selectedKey={selectedKey}
                onSelect={openDoc}
                onCopy={handleCopy}
                hasFilters={activeFilterCount > 0}
            />

            {totalPages > 1 && (
                <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>
                        Page {page} of {totalPages} · {formatCount(CREDIT_NOTE_PAGE_SIZE)} per page
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(Math.max(1, page - 1))}
                            disabled={page <= 1}
                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-3 disabled:opacity-40"
                        >
                            <ChevronLeft className="h-4 w-4" /> Previous
                        </button>
                        <button
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages}
                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-3 disabled:opacity-40"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            <p className="text-[11px] leading-4 text-gray-400">
                Links, left to right: the customer in HubSpot · the ERP's own DocuWare cross-reference
                (grey — it repeats the credit note number and is not an archive id, so no link is built
                from it) · the document in Business Central. dbi and bin run redPoint and have no
                Business Central web client, so BC stays grey there.
                A credit note is a posted sales credit memo in the company's ERP — never a negative
                amount; commission reversals and the dfr “AEN” documents are not counted.
            </p>

            </>
            )}

            {/* Outside the tab switch on purpose: a click in the recourse queue opens this same
                drawer, and closing it must not depend on which tab the reader came from. */}
            {selectedDoc && (
                <CreditNoteDrawer
                    doc={selectedDoc}
                    skipReasonLabels={summary.skipReasonLabels}
                    canEdit={canEdit}
                    onClose={closeDoc}
                    onCopy={handleCopy}
                    onStateChanged={handleStateChanged}
                />
            )}
        </div>
    );
}

function TabButton({
    active, onClick, label, badge, badgeTone,
}: {
    active: boolean;
    onClick: () => void;
    label: string;
    badge?: string;
    badgeTone?: 'normal' | 'alert';
}) {
    return (
        <button
            onClick={onClick}
            className={`border-b-2 px-6 py-3 text-sm font-medium ${
                active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
            {label}
            {badge && (
                <span className={`ml-2 rounded px-1.5 py-0.5 text-[11px] ${
                    badgeTone === 'alert' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}>
                    {badge}
                </span>
            )}
        </button>
    );
}

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'warn' }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3">
            <div className="text-xs text-gray-500">{label}</div>
            <div className={`mt-0.5 text-[22px] font-semibold leading-7 ${tone === 'warn' ? 'text-amber-700' : 'text-gray-900'}`}>
                {value}
            </div>
            {hint && <div className="text-[11px] leading-4 text-gray-400">{hint}</div>}
        </div>
    );
}
